import {
    api,
    deleteWithOptionalAuthorization,
    getWithOptionalAuthorizationGeneric,
    postWithOptionalAuthorization,
} from "./api";
import { NOTIFICATION_BASE } from "./apiConstant"; // 🔹 Define your base URL constant here
import { getToken } from "./auth";
import { decryptMessage } from "../utils/decrypt";

// Notification payload type
export type NotificationMobilePayload = {
    id?: string; // Optional for new notifications
    campaignName: string;
    status: number; // 0 = Draft, 1 = Publish
    startDate: string; // MM/DD/YYYY
    startTime: string;
    endDate?: string; // optional
    endTime?: string; // optional
    createdAt?: string;
    inAppNotification: boolean;
    pushNotification: boolean;
    emailNotification: boolean;
    title: string;
    subTitle: string;
    subjectLine?: string;
    emailText?: string;
    description: string;
    announcementImagesIDs?: string[];
};

// API service
const notificationMobileApi = {

    getNotificationById: async (ID: number): Promise<NotificationMobilePayload> => {
        try {
            const encryptedContestResponse = await getWithOptionalAuthorizationGeneric<NotificationMobilePayload[]>(`${NOTIFICATION_BASE}/${ID}`, true);
            const response = await decryptMessage(encryptedContestResponse.data.Data, getToken());
            return response;
        } catch (error) {
            console.error('Error fetching notification:', error);
            throw error;
        }
    },

    /**
     * Save Notification (Draft or Publish)
     */
    saveNotification: async (
        payload: NotificationMobilePayload
    ): Promise<any> => {
        try {
            const response = await postWithOptionalAuthorization(
                `${NOTIFICATION_BASE}`, // 
                payload,
                true,
                false
            );
            const token = getToken();
            if (!token) throw new Error("No token found");

            const decryptedData = await decryptMessage(response.data.Data, token);
            //const announcementSupportId = decryptedData.id;
            return decryptedData;

        } catch (error: any) {
            console.error("Error saving Notification Mobile:", error.response);
            throw error;
        }
    },

    /**
    * Update existing notification
    */
    updateNotification: async (payload: NotificationMobilePayload): Promise<any> => {
        try {
            const token = getToken();
            if (!token) throw new Error("No token found");

            const response = await api.put(
                `${NOTIFICATION_BASE}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const decryptedData = await decryptMessage(response.data.Data, token);
            return decryptedData;
        } catch (error: any) {
            console.error("Error updating Notification Mobile:", error.response);
            throw error;
        }
    },

    updateFinalDescriptionNotification: async (payload: NotificationMobilePayload): Promise<any> => {
        try {
            const token = getToken();
            if (!token) throw new Error("No token found");

            const response = await api.patch(
                `${NOTIFICATION_BASE}/Description`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const decryptedData = await decryptMessage(response.data.Data, token);
            return decryptedData;
        } catch (error: any) {
            console.error("Error updating Notification Mobile:", error.response);
            throw error;
        }
    },


    // Upload image for CKEditor
    uploadImage: async (announcementSupportId: string, file: File): Promise<any> => {
        try {
            const formData = new FormData();
            formData.append("AnnouncementFile", file);
            formData.append("AnnouncementSupportID", announcementSupportId);

            const response = await postWithOptionalAuthorization(
                `${NOTIFICATION_BASE}/AnnouncementSupportImages`,
                formData,
                true,
                true,
            );

            const token = getToken();
            if (!token) throw new Error("No token found");

            const decryptedData = await decryptMessage(response.data.Data, token);
            return decryptedData;
        } catch (error: any) {
            console.error("Error uploading image:", error.response);
            throw error;
        }
    },

    getNotificationsList: async (
        status?: string
    ): Promise<{ data: NotificationMobilePayload[]; total: number }> => {
        try {
            const params = new URLSearchParams();
            if (status) params.append("Status", status);

            const url = `${NOTIFICATION_BASE}?${params.toString()}`;
            const encryptedResponse = await getWithOptionalAuthorizationGeneric<any[]>(url, true);

            const token = getToken();
            if (!token) throw new Error("No token found");

            const decryptedData = await decryptMessage(encryptedResponse.data.Data, token);
            // Return full dataset (client will paginate)
            const items = decryptedData.items || decryptedData;
            return {
                data: items,
                total: items.length,
            };
        } catch (error) {
            console.error("Error fetching Notifications:", error);
            throw error;
        }
    },

    deleteNotification: async (id: string): Promise<void> => {
        try {
            const token = getToken();
            if (!token) throw new Error("No token found");

            const url = `${NOTIFICATION_BASE}/${id}`;
            await deleteWithOptionalAuthorization(url, true);
        } catch (error) {
            console.error("Error deleting notification:", error);
            throw error;
        }
    },

    cloneNotification: async (data: any): Promise<any> => {
        try {
            const response = await postWithOptionalAuthorization(`${NOTIFICATION_BASE}/ClonedAnnouncement`, data, true);
            return response.data;
        } catch (error) {
            console.error("Error creating notification:", error);
            throw error;
        }
    },
};

export default notificationMobileApi;
